import * as React from 'react';

// Injected per-site configuration. Lets the shared library render correctly for
// either market (German .de / .de/en/ and the future US .com) without baking
// site-specific values into @sorpetaler/ui. Each site provides its own config
// via SiteConfigProvider; adding a required field here makes both site builds
// fail to type-check until each supplies it — sync enforced by the type system.
export interface SiteConfig {
  locale: 'De' | 'En' | 'Us';
  /** Canonical origin, e.g. https://www.sorpetaler.de | https://www.sorpetaler.com */
  baseUrl: string;
  brand: {
    legalName: string;
    /**
     * Optional contact phone. `display` is the visible international-format text,
     * `href` the international tel: target, `localHref` the local-format tel: target
     * used in the compact widget/mobile links.
     */
    phone?: { display: string; href: string; localHref: string };
    socialLinks: { title: string; url: string }[];
  };
  /** Base Metrica analytics URL (German analytics; omitted/replaced per market). */
  metricaUrl: string;
  /** Human breadcrumb labels for known path segments. */
  breadcrumbLabels: Record<string, string>;
  /** URL prefix for a given LanguageContext value (De:'/' En:'/en/' Us:'/'). */
  routePrefix: (language: string) => string;
  /** Slugs identifying special categories, per market. */
  categorySlugs: { references: string[]; fuerProfis: string[] };
  /**
   * Selective cross-domain hreflang (SEO Option 3: homepage-only at launch).
   * When set, it replaces the generated De/En hreflang scheme: a page whose
   * normalized pathname matches a key of `entriesByPath` emits exactly those
   * entries; every other page emits a single self-referential tag with
   * `selfHrefLang`. Omitted on the German site, so De/En output is unchanged.
   */
  hreflang?: {
    entriesByPath: Record<string, { hrefLang: string; href: string }[]>;
    selfHrefLang: string;
  };
  /**
   * Optional soft geo-suggestion banner (SEO Option 3: no hard IP redirects).
   * Shown client-side only, when `shouldSuggest(navigator.language)` returns
   * true and the visitor hasn't dismissed it (dismissal persisted under
   * `storageKey` in localStorage). `onlyForLanguage` restricts it to pages of
   * one LanguageContext value — the German site suggests the US site on its
   * /en/ pages only.
   */
  geoSuggestion?: {
    message: string;
    linkTitle: string;
    linkHref: string;
    storageKey: string;
    shouldSuggest: (browserLanguage: string) => boolean;
    onlyForLanguage?: 'De' | 'En' | 'Us';
  };
}

// Neutral default so the library never hard-codes a market. The German site
// (and, later, the US site) overrides this via SiteConfigProvider at the root,
// so production builds always read injected values.
const DEFAULT_SITE_CONFIG: SiteConfig = {
  locale: 'De',
  baseUrl: '',
  brand: { legalName: '', socialLinks: [] },
  metricaUrl: '',
  breadcrumbLabels: {},
  routePrefix: (language) => (language === 'De' ? '/' : '/en/'),
  categorySlugs: { references: [], fuerProfis: [] },
};

const SiteConfigContext = React.createContext<SiteConfig>(DEFAULT_SITE_CONFIG);

export const SiteConfigProvider = ({
  config,
  children,
}: {
  config: SiteConfig;
  children: React.ReactNode;
}): React.ReactElement => (
  <SiteConfigContext.Provider value={config}>
    {children}
  </SiteConfigContext.Provider>
);

export const useSiteConfig = (): SiteConfig => React.useContext(SiteConfigContext);
